export const asText = (value) => String(value ?? '').trim();

export const asList = (value) => {
  if (Array.isArray(value)) return value.map(asText).filter(Boolean);
  if (value === undefined || value === null || value === '') return [];
  if (typeof value === 'string' && value.includes(',')) return value.split(',').map(asText).filter(Boolean);
  return [asText(value)].filter(Boolean);
};

export const fullUserName = (user = {}) => asText(
  user.displayName || user.name || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'TELCOFAIR User'
);

export const userBusinessProfile = (user = {}, company = {}) => ({
  specialization: asList(user.specialization || user.specializations || user.businessAreas || user.industries || company.specialization || company.specializations),
  primaryServices: asList(user.primaryServiceProvided || user.primaryServices || user.primaryService || company.primaryServiceProvided || company.primaryService),
  secondaryServices: asList(user.secondaryServiceProvided || user.secondaryServices || user.secondaryService || company.secondaryServiceProvided || company.secondaryService),
  primaryRegions: asList(user.primaryRegionAvailability || user.primaryRegions || user.primaryRegion || company.primaryRegionAvailability || company.primaryRegion),
  secondaryRegions: asList(user.secondaryRegionAvailability || user.secondaryRegions || user.secondaryRegion || company.secondaryRegionAvailability || company.secondaryRegion),
  primaryNeeds: asList(user.primaryServiceBuying || user.servicesSought || user.lookingFor || user.buyingInterests || company.primaryServiceBuying || company.primaryConnectWith),
  secondaryNeeds: asList(user.secondaryServiceBuying || company.secondaryServiceBuying || company.secondaryConnectWith || company.otherConnectWith),
  country: asText(user.country || user.countryName || company.country || company.region),
  companyName: asText(user.companyName || company.companyName || company.name),
  jobTitle: asText(user.jobTitle || user.businessTitle || user.designation || user.title || user.seniorityLevel),
  description: asText(user.professionalSummary || user.businessDescription || user.bio || user.about || company.description),
});

const norm = (value) => asText(value).toLowerCase().replace(/[^a-z0-9+.#/& -]/g, '').replace(/\s+/g, ' ').trim();

const matchLists = (a, b) => {
  const one = asList(a);
  const two = asList(b);
  const matches = [];
  one.forEach((left) => {
    const nl = norm(left);
    if (!nl) return;
    const found = two.find((right) => {
      const nr = norm(right);
      return nr && (nl === nr || nl.includes(nr) || nr.includes(nl));
    });
    if (found && !matches.some((x) => norm(x) === nl)) matches.push(left);
  });
  const denominator = Math.max(1, Math.min(new Set(one.map(norm).filter(Boolean)).size, new Set(two.map(norm).filter(Boolean)).size));
  return { matches, ratio: Math.min(1, matches.length / denominator) };
};

export const calculateLocalBusinessFit = (senderUser = {}, targetUser = {}, senderCompany = {}, targetCompany = {}) => {
  const sender = userBusinessProfile(senderUser, senderCompany);
  const target = userBusinessProfile(targetUser, targetCompany);
  const checks = [
    { key: 'offerToNeedPrimary', label: 'Your primary services match their demand', weight: 22, ...matchLists(sender.primaryServices, target.primaryNeeds) },
    { key: 'offerToNeedSecondary', label: 'Your secondary services match their demand', weight: 12, ...matchLists(sender.secondaryServices, [...target.primaryNeeds, ...target.secondaryNeeds]) },
    { key: 'needToOfferPrimary', label: 'Their primary services match what you need', weight: 22, ...matchLists(target.primaryServices, sender.primaryNeeds) },
    { key: 'needToOfferSecondary', label: 'Their secondary services match what you need', weight: 12, ...matchLists(target.secondaryServices, [...sender.primaryNeeds, ...sender.secondaryNeeds]) },
    { key: 'specialization', label: 'Shared specialization', weight: 12, ...matchLists(sender.specialization, target.specialization) },
    { key: 'primaryRegions', label: 'Shared primary regions', weight: 12, ...matchLists(sender.primaryRegions, target.primaryRegions) },
    { key: 'secondaryRegions', label: 'Shared secondary regions', weight: 6, ...matchLists([...sender.primaryRegions, ...sender.secondaryRegions], [...target.primaryRegions, ...target.secondaryRegions]) },
  ];
  let score = checks.reduce((sum, item) => sum + item.weight * item.ratio, 0);
  if (sender.country && target.country && norm(sender.country) === norm(target.country)) score += 2;
  score = Math.min(100, Math.round(score));
  const reasons = checks.filter((x) => x.matches.length).map((x) => `${x.label}: ${x.matches.join(', ')}`);
  return { score, reasons, breakdown: checks.map(({ key, label, weight, matches, ratio }) => ({ key, label, weight, matches, ratio })), sender, target };
};

export const compactList = (items, max = 2) => {
  const list = asList(items);
  if (!list.length) return 'Not specified';
  const shown = list.slice(0, max).join(', ');
  return list.length > max ? `${shown} +${list.length - max}` : shown;
};
